import { useMemo, useState } from "react"
import { DataTable, EmptyState, ErrorState, MoneyText } from "procurement/design"
import { useGetBudgetDashboardAllocationByCenterQuery } from "shared/redux/endpoints/procurementV2"
import { pctOf } from "./chartSetup"
import { CenterPicker, useCenterPicker } from "./CenterPicker"

const AMOUNT_KEYS = ["totalBudget", "committed", "unpaid", "paid", "usedAmount", "remaining"]

const money = (key) => (r) => r[key] == null ? "—" : <MoneyText value={r[key]} />

const COLUMNS = [
  {
    key: "name",
    header: "Center / Category / Item",
    className: "pc-col-title",
    render: (r) => <span style={{ paddingLeft: r.depth > 1 ? 16 : 0 }}>{r.name}</span>,
  },
  {
    key: "totalBudget",
    header: "Budget",
    align: "right",
    render: money("totalBudget"),
  },
  {
    key: "committed",
    header: "Committed",
    align: "right",
    render: money("committed"),
  },
  { key: "unpaid", header: "Unpaid", align: "right", render: money("unpaid") },
  { key: "paid", header: "Paid", align: "right", render: money("paid") },
  {
    key: "usedAmount",
    header: "Used",
    align: "right",
    render: money("usedAmount"),
  },
  {
    key: "remaining",
    header: "Remaining",
    align: "right",
    render: money("remaining"),
  },
  {
    key: "usedPercentage",
    header: "Used %",
    align: "right",
    render: (r) => pctOf(r.usedPercentage),
  },
  {
    key: "orLineCount",
    header: "OR lines (with PO)",
    align: "right",
    render: (r) => (r.orLineCount == null ? "—" : `${r.orLineCount} (${r.orLinesWithPo ?? 0})`),
  },
  {
    key: "orRequestedAmount",
    header: "OR requested (ref.)",
    align: "right",
    tooltip: "Reference from OR lines only — payment allocation per item is pending on the backend",
    render: money("orRequestedAmount"),
  },
]

// Only CENTER_CATEGORY rows carry budget money; ITEM_DETAIL rows are OR
// references and must never be summed into the center totals.
const buildTree = (items, fallbackCategoryId) => {
  const centers = new Map()
  const itemsByParent = new Map()
  items.forEach((r) => {
    if (r.rowType === "ITEM_DETAIL") {
      const categoryId = r.budgetCategoryId ?? fallbackCategoryId
      const parent = categoryId ? `cat-${r.budgetCenterId}-${categoryId}` : `ctr-${r.budgetCenterId}`
      if (!itemsByParent.has(parent)) itemsByParent.set(parent, [])
      itemsByParent.get(parent).push({
        ...r,
        key: `item-${r.budgetCenterId}-${r.itemId}-${parent}`,
        name: r.itemName,
      })
      if (!centers.has(r.budgetCenterId)) {
        centers.set(r.budgetCenterId, {
          key: `ctr-${r.budgetCenterId}`,
          name: r.budgetCenterName,
          categories: [],
        })
      }
      return
    }
    if (!centers.has(r.budgetCenterId)) {
      centers.set(r.budgetCenterId, {
        key: `ctr-${r.budgetCenterId}`,
        name: r.budgetCenterName,
        categories: [],
      })
    }
    centers.get(r.budgetCenterId).categories.push({
      ...r,
      key: `cat-${r.budgetCenterId}-${r.budgetCategoryId}`,
      name: r.categoryName,
    })
  })
  const tree = [...centers.values()].map((c) => {
    const totals = Object.fromEntries(
      AMOUNT_KEYS.map((k) => [
        k,
        c.categories.length ? c.categories.reduce((s, x) => s + (Number(x[k]) || 0), 0) : null,
      ])
    )
    return {
      ...c,
      ...totals,
      usedPercentage: totals.totalBudget > 0 ? (totals.usedAmount / totals.totalBudget) * 100 : null,
    }
  })
  return { tree, itemsByParent }
}

const CenterAllocationTree = ({ params }) => {
  const [expanded, setExpanded] = useState(() => new Set())
  const ready = Boolean(params.centerId || params.categoryId)
  const { data, isFetching, isError } = useGetBudgetDashboardAllocationByCenterQuery(
    { ...params, size: 1000 },
    { skip: !ready }
  )

  const items = useMemo(() => data?.items || [], [data])
  const { filtered, pickerProps } = useCenterPicker(items)

  const rows = useMemo(() => {
    const { tree, itemsByParent } = buildTree(filtered, params.categoryId)
    const out = []
    tree.forEach((c) => {
      const centerItems = itemsByParent.get(c.key) || []
      out.push({
        ...c,
        depth: 0,
        expandable: c.categories.length + centerItems.length > 0,
      })
      if (!expanded.has(c.key)) return
      c.categories.forEach((cat) => {
        const items = itemsByParent.get(cat.key) || []
        out.push({ ...cat, depth: 1, expandable: items.length > 0 })
        if (expanded.has(cat.key)) items.forEach((it) => out.push({ ...it, depth: 2 }))
      })
      centerItems.forEach((it) => out.push({ ...it, depth: 1 }))
    })
    return out
  }, [filtered, expanded, params.categoryId])

  const toggle = (row) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(row.key)) next.delete(row.key)
      else next.add(row.key)
      return next
    })

  if (!ready)
    return <EmptyState title="Pick a center or a category" text="This breakdown needs at least one of them." />
  if (isError) return <ErrorState text="Couldn't load the allocation by center." />

  return (
    <>
      {items.length > 0 && (
        <div className="br-pad-picker">
          <CenterPicker {...pickerProps} />
        </div>
      )}
      <DataTable
        columns={COLUMNS}
        rows={rows}
        isLoading={isFetching}
        sortRows={false}
        expandedKeys={expanded}
        onToggleExpand={toggle}
        density="compact"
        // DataTable resets to page 1 whenever rows change, so paging would jump on every expand.
        hidePagination
        empty={<EmptyState title="No allocation for these filters." />}
      />
    </>
  )
}

export default CenterAllocationTree
